import { useCallback, useEffect, useMemo, useState, type Context, type ReactNode } from "react";
import type { Wallet, WalletAccount } from "@wallet-standard/base";
import type { AuthResponse } from "@redchain/contracts";
import { api } from "../../api/client";
import { env } from "../../env";
import type { GameWallet, WalletContextValue } from "../types";
import { connectWallet, disconnectWallet, lastExternalWallet, listExternalWallets, onWalletsChanged } from "../walletStandard";
import { CustodialApprovalHost } from "../../wallet-menu/CustodialApprovalHost";
import { clearCustodySession, loadLooseMe } from "./api";
import { setApprovalHandler } from "./approval";
import { custodialGameWallet, gameAccountFromAuth } from "./gameWallet";

function friendlyWalletError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (/chain id does not match/i.test(msg)) {
    return `This site uses Solana ${env.cluster}. Switch your wallet to ${env.cluster} (Phantom: Settings → Developer Settings → Testnet Mode / Change Network), then try again.`;
  }
  return msg;
}

/**
 * Connected-wallet sign-in with a custodial game account (§8.1).
 * Mounted only when VITE_WALLET_MODE=custodial.
 */
export function CustodialWalletProvider({
  children,
  context,
}: {
  children: ReactNode;
  context: Context<WalletContextValue | null>;
}) {
  const [external, setExternal] = useState<{ wallet: Wallet; account: WalletAccount } | null>(null);
  const [gameAccount, setGameAccount] = useState<string | null>(null);
  const [auth, setAuth] = useState<AuthResponse | null>(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalList, setExternalList] = useState(() => listExternalWallets());
  const [restored, setRestored] = useState(false);
  const [approval, setApproval] = useState<{ message: string; resolve: (ok: boolean) => void } | null>(null);

  useEffect(() => onWalletsChanged(() => setExternalList(listExternalWallets())), []);

  useEffect(() => {
    api.onSignedOut = () => {
      setAuth(null);
      setGameAccount(null);
    };
  }, []);

  useEffect(() => {
    if (!external) {
      setApprovalHandler(null);
      return;
    }
    setApprovalHandler(
      (message) =>
        new Promise<boolean>((resolve) => {
          setApproval({ message, resolve });
        }),
    );
    return () => setApprovalHandler(null);
  }, [external]);

  const wallet = useMemo<GameWallet | null>(
    () => (external && gameAccount ? custodialGameWallet(external.wallet, external.account, gameAccount) : null),
    [external, gameAccount],
  );

  useEffect(() => {
    if (restored) return;
    setRestored(true);
    const name = lastExternalWallet();
    if (!name) return;
    void (async () => {
      try {
        const conn = await connectWallet(name, true);
        const token = await api.refresh();
        if (!token) return;
        const me = await loadLooseMe();
        const game = me.wallet || conn.account.address;
        setExternal(conn);
        setGameAccount(game);
        setAuth({ accessToken: token, player: { registered: me.registered ?? false }, wallet: game });
      } catch {
        /* user will log in again */
      }
    })();
  }, [restored]);

  const connectExternal = useCallback(async (name: string) => {
    setAuthenticating(true);
    setError(null);
    try {
      const conn = await connectWallet(name);
      const gw = custodialGameWallet(conn.wallet, conn.account, conn.account.address);
      const res = await gw.signIn();
      const game = gameAccountFromAuth(res, conn.account.address);
      setExternal(conn);
      setGameAccount(game);
      setAuth({ ...res, wallet: game });
    } catch (e) {
      setError(friendlyWalletError(e));
    } finally {
      setAuthenticating(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    if (external) await disconnectWallet(external.wallet);
    clearCustodySession();
    setExternal(null);
    setGameAccount(null);
    setAuth(null);
  }, [external]);

  const settleApproval = (ok: boolean) => {
    approval?.resolve(ok);
    setApproval(null);
  };

  const value: WalletContextValue = {
    ready: true,
    wallet,
    auth,
    authenticating,
    error,
    externalWallets: externalList.map((w) => ({ name: w.name, icon: w.icon })),
    connectExternal,
    logout,
    markRegistered: () => setAuth((a) => (a ? { ...a, player: { registered: true } } : a)),
  };

  return (
    <context.Provider value={value}>
      {children}
      <CustodialApprovalHost message={approval?.message ?? null} onConfirm={() => settleApproval(true)} onCancel={() => settleApproval(false)} />
    </context.Provider>
  );
}
